/**
 * Static marketing copy for the public pricing surfaces (landing Pricing section and /pricing page).
 *
 * Prices, the listing period and the trial length are NOT here — they come from
 * GET /api/public/pricing at page load. The wording of what a plan, the listing offering and agent
 * support include is the shared PLAN_COPY (@resida/api-types/plan-copy), imported at build time so
 * it is in the prerendered HTML; the only figure taken from it is the per-payment ACH fee.
 */
import { ACH_PAYMENT_FEE_CENTS } from '@resida/api-types/plan-copy';
import { formatPriceCents } from '../hooks/usePublicPricing';

const ACH_PAYMENT_PRICE = formatPriceCents(ACH_PAYMENT_FEE_CENTS);

/** Card titles, unit labels and call-to-action wording shared by the pricing cards. */
export const PRICING_CARD_COPY = {
  perUnitMonth: 'per unit / month',
  perListing: 'per listing',
  ctaLabel: 'Start Free Trial',
  loadingLabel: 'Loading pricing',
  comingSoonTitle: 'Pricing coming soon',
  errorTitle: "We couldn't load prices",
  errorBody: 'Check your connection and try again.',
  retryLabel: 'Retry',
  includedHeading: 'Every plan includes',
} as const;

/** Unit line under the listing price; the live period comes from the pricing payload. */
export function listingUnitText(periodDays: number | null | undefined): string {
  return periodDays
    ? `${PRICING_CARD_COPY.perListing} · ${periodDays} days live`
    : PRICING_CARD_COPY.perListing;
}

/** The free-trial pill text; the length comes from the pricing payload, no number until known. */
export function trialPillText(durationDays: number | null | undefined): string {
  return durationDays
    ? `${durationDays}-day free trial — no credit card required`
    : 'Free trial — no credit card required';
}

/** SEO and hero copy for the /pricing page. */
export const PRICING_PAGE_COPY = {
  seoTitle: 'Pricing',
  seoDescription: `Simple, transparent pricing for independent landlords. Per-unit pricing with ACH payments at ${ACH_PAYMENT_PRICE}, plus optional assisted listing posting and agent support. Free trial with no credit card required.`,
  heading: 'Simple, transparent pricing',
  plansHeading: 'Plans',
  breadcrumbHome: 'Home',
  breadcrumbCurrent: 'Pricing',
  faqHeading: 'Pricing questions',
  intro: `A simple per-unit plan, with ACH payments at ${ACH_PAYMENT_PRICE} each. Assisted listing posting and agent support are optional, with their own prices.`,
} as const;

/** Heading and intro of the landing page's Pricing section. */
export const PRICING_SECTION_COPY = {
  heading: 'Simple, transparent pricing',
  intro: `Simple per-unit pricing, with ACH payments at ${ACH_PAYMENT_PRICE}. Assisted listing posting and agent support are optional, priced on their own.`,
} as const;

/** The /pricing page FAQ; also the source of its FAQPage structured data. */
export const PRICING_FAQ: readonly { question: string; answer: string }[] = [
  {
    question: 'What does the Resida plan include?',
    answer:
      'Rent collection, leases, e-signatures, expense tracking, maintenance, communication, and reporting. Plans and their prices are set up by Resida, so the plan shown here may change over time. Assisted listing posting and agent support are optional extras with their own simple prices.',
  },
  {
    question: 'When do I need a credit card?',
    answer:
      'Not to start — no credit card is collected during your free trial. You add a card when you subscribe to a plan, or the first time you publish a listing or turn on agent support.',
  },
  {
    question: 'What happens after the free trial?',
    answer:
      "You choose whether to subscribe. No credit card is collected during the trial, so there's nothing to cancel. Your data stays available if you decide to subscribe later.",
  },
  {
    question: 'What if my unit rents in a week?',
    answer:
      "The listing fee covers the live period of the assisted posting on Craigslist and Facebook. If the unit fills early you can pause or close the listing for free, but the fee isn't refunded for the unused days. Nothing renews automatically — we remind you before a listing ends.",
  },
  {
    question: 'How is agent support billed?',
    answer:
      "It's an optional add-on billed per unit each month with your plan. When you turn it on you pay a prorated amount for the rest of the current billing period. Turn it off any time and it stays on until that period ends.",
  },
  {
    question: 'Are there per-transaction fees?',
    answer: `ACH payments are ${ACH_PAYMENT_PRICE} each. Beyond that, Resida charges a per-unit subscription, plus the optional listing and agent support prices shown above.`,
  },
  {
    question: 'What if I have more than 20 units?',
    answer:
      "Resida is designed for landlords with 1 to 20 units. If you're managing more than that, reach out — we can discuss whether Resida is the right fit for your portfolio size.",
  },
];

/** The editable inputs of the /pricing savings calculator. */
export type SavingsFieldKey =
  | 'units'
  | 'rent'
  | 'zelleShare'
  | 'managementFee'
  | 'hoursPerUnit'
  | 'hourlyValue'
  | 'esignMonthly';

/**
 * Starting values for the calculator. Money is in cents so no dollar literal lives in source; every
 * figure is an estimate the visitor can change, and none is a Resida price (those come from the API).
 */
export const SAVINGS_DEFAULTS = {
  units: 5,
  rentCents: 150000,
  zelleSharePercent: 50,
  managementFeePercent: 10,
  hoursPerUnit: 1,
  hourlyValueCents: 2500,
  esignMonthlyCents: 1100,
} as const;

/** Bounds the calculator accepts; Resida is built for 1 to 20 units. */
export const SAVINGS_LIMITS = { minUnits: 1, maxUnits: 20 } as const;

/** DOM id of a calculator input; its test id is `${id}-input`. */
export function savingsFieldId(key: SavingsFieldKey): string {
  return `savings-${key}`;
}

export interface SavingsField {
  key: SavingsFieldKey;
  label: string;
  /** Unit hint shown beside the label, e.g. "per month". */
  hint?: string;
}

/** The two portfolio inputs and the Zelle/External share, always visible. */
export const SAVINGS_PRIMARY_FIELDS: readonly SavingsField[] = [
  { key: 'units', label: 'Number of units' },
  { key: 'rent', label: 'Average monthly rent per unit', hint: 'dollars' },
  { key: 'zelleShare', label: 'Rent paid by Zelle or External Payments', hint: 'percent' },
];

/** The assumptions behind each savings line, editable under a disclosure. */
export const SAVINGS_ASSUMPTION_FIELDS: readonly SavingsField[] = [
  { key: 'managementFee', label: 'Property manager fee', hint: 'percent of rent' },
  { key: 'hoursPerUnit', label: 'Hours you spend per unit', hint: 'per month' },
  { key: 'hourlyValue', label: 'Value of an hour of your time', hint: 'dollars' },
  { key: 'esignMonthly', label: 'Standalone e-signature plan', hint: 'dollars per month' },
];

/** Headings, line-item labels and footnote of the savings calculator. */
export const SAVINGS_COPY = {
  heading: 'What Resida saves you',
  intro:
    'Put your own portfolio in and see what you keep each month after the platform cost. Every figure is an estimate you can change.',
  inputsHeading: 'Your portfolio',
  assumptionsSummary: 'Adjust the assumptions',
  savingsHeading: 'You save',
  costHeading: 'Resida costs',
  planBasis: 'Plan cost uses our lowest plan, starting at',
  managerLine: 'Property manager fee you avoid',
  timeLine: 'Your time back',
  esignLine: 'Standalone e-signature plan you skip',
  savingsTotal: 'Total savings',
  platformLine: 'Resida plan',
  achLine: 'ACH rent payments',
  zelleNote: 'Rent paid by Zelle or External Payments has no payment fee, so more of it lowers your cost.',
  costTotal: 'Total cost',
  netMonthly: 'Net savings per month',
  netAnnual: 'Net savings per year',
  perMonth: 'per month',
  invalidInputs: 'Enter a whole number of units within the supported range and a rent amount to see your savings.',
  footnote:
    'Estimates only, not a guarantee. They assume each unit pays rent once a month, that a property manager would charge the percentage above, and that you would otherwise buy a standalone e-signature plan. Change any assumption to match your situation.',
} as const;
